import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Truck, Users, Package, LogOut, Plus, Search,
  User, CheckCircle, Clock, Menu, X, Lock, 
  Shield, Ban, Box, 
  ClipboardList, Building, UserCheck, FileText, 
  Mail, ClipboardCheck, Wifi,
  FileSpreadsheet, Download, Calendar, Activity,
  BarChart3, TrendingUp, Timer, Key, Bell, ChevronsLeft, ChevronsRight, Trash2, Eye
} from 'lucide-react';
import { collection, query, where, onSnapshot, getDocs, addDoc, updateDoc } from 'firebase/firestore';
import { useAuth } from './contexts/AuthContext';
import { db, getDeliveriesRef, getDeliveryDoc, SYSTEM_ID, validateUserPin, deleteDelivery, getDeletionAuditLogs, deleteAuditLog } from './lib/firebase';
import { getDisplayName } from './utils/profile';
import { RecipientCombobox } from './components/RecipientCombobox';
import { buildNewDeliveryState, portariaCanDeliverWithPin } from './deliveryFlow';
import { useToast } from './components/ToastProvider';

// --- ESTILOS CUSTOMIZADOS ---
const customStyles = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in-up {
    animation: fadeInUp 0.4s ease-out forwards;
  }
  .glass-effect {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
  }
  .sidebar-gradient {
    background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
  }
  .sidebar-nav-active {
    box-shadow: 0 10px 28px rgba(79, 70, 229, 0.35);
  }
  @keyframes rowFlash {
    0%   { background-color: rgba(16, 185, 129, 0.18); }
    60%  { background-color: rgba(16, 185, 129, 0.08); }
    100% { background-color: transparent; }
  }
  .animate-row-flash { animation: rowFlash 1.2s ease-out forwards; }

  @keyframes successPulse {
    0%   { transform: scale(0.6); opacity: 0; }
    50%  { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
  .animate-success-pulse { animation: successPulse 0.45s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

  @media (prefers-reduced-motion: reduce) {
    .sidebar-nav-active {
      box-shadow: 0 4px 12px rgba(79, 70, 229, 0.25);
    }
    .animate-row-flash,
    .animate-success-pulse {
      animation: none;
    }
  }
`;

// --- HELPER DATE ---
const getISODate = () => new Date().toISOString();
const formatDate = (val) => {
  if (!val) return '-';
  try {
    if (typeof val === 'object' && val.toDate) {
      return val.toDate().toLocaleDateString('pt-BR') + ' ' + val.toDate().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    const date = new Date(val);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return '-';
  }
};

// --- HELPER DE PERMISSÃO (login único: roles em userProfile.roles[SYSTEM_ID]) ---
const hasRole = (userProfile, roleName) => {
  if (!userProfile?.roles) return false;
  const role = userProfile.roles[SYSTEM_ID];
  if (!role || role === 'none' || role === '') return false;
  if (roleName === 'dono') return role === 'colaborador';
  if (roleName === 'recepcao') return role === 'recepção';
  return role === roleName;
};

/**
 * Verifica se o usuário é admin global (isAdmin no documento users/{uid}).
 * Aceita boolean true ou string 'true' (ambas as formas usadas no backend).
 * Diferente de hasRole(userProfile, 'admin') que verifica roles.sistema_amc_log.
 */
const isGlobalAdmin = (userProfile) => {
  return userProfile?.isAdmin === true || userProfile?.isAdmin === 'true';
};

export const SECURITY_DELIVERY_MODE = {
  OWNER: 'owner',
  THIRD_PARTY: 'third_party',
};

export const getSecurityValidationTargetUid = ({ securityType, deliveryMode, delivery, receiverUid, currentUserUid }) => {
  if (securityType === 'owner') {
    return deliveryMode === SECURITY_DELIVERY_MODE.THIRD_PARTY ? receiverUid : delivery?.ownerUid;
  }
  return currentUserUid;
};

export const buildSecureDeliveryReceipt = ({ delivery, deliveryMode, receiver }) => {
  if (deliveryMode === SECURITY_DELIVERY_MODE.THIRD_PARTY && receiver) {
    return {
      receivedByUid: receiver.id,
      receivedByName: getDisplayName(receiver),
      receivedByUsername: receiver.email || receiver.username || '',
      receivedByType: SECURITY_DELIVERY_MODE.THIRD_PARTY,
      note: `Entrega final confirmada por ${getDisplayName(receiver)}, retirando em nome de ${String(delivery.owner_name || 'Destinatário original')}`,
    };
  }

  return {
    receivedByUid: delivery.ownerUid || '',
    receivedByName: String(delivery.owner_name || ''),
    receivedByUsername: String(delivery.owner_username || ''),
    receivedByType: SECURITY_DELIVERY_MODE.OWNER,
    note: 'Entrega final confirmada com PIN do destinatário',
  };
};

const getRoleLabel = (profile) => {
  const r = profile?.roles?.[SYSTEM_ID];
  if (!r || r === 'none') return 'Sem acesso';
  const labels = { admin: 'Administrador', colaborador: 'Colaborador', portaria: 'Portaria', 'recepção': 'Recepção' };
  return labels[r] || r;
};

// --- CONFIGURAÇÃO DE STATUS ---
const STATUS_CONFIG = {
  'received_portaria': { label: 'Na Portaria', color: 'text-slate-600', bg: 'bg-slate-100 border-slate-200', icon: Box },
  'transit_to_reception': { label: 'Em Trânsito', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: Truck, animate: 'animate-pulse' },
  'at_reception': { label: 'Na Recepção', color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200', icon: Building },
  'waiting_confirmation': { label: 'Pendente (legado)', color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200', icon: UserCheck, animate: 'animate-pulse' },
  'delivered': { label: 'Entregue', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle },
};

// --- COMPONENTES AUXILIARES ---
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG['received_portaria'];
  const Icon = config.icon;
  return (
    <span className={`px-3 py-1.5 rounded-full text-xs font-bold border flex items-center gap-2 w-fit shadow-sm transition-all duration-300 hover:scale-105 ${config.bg} ${config.color} ${config.animate || ''}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
};

const Timeline = ({ history }) => {
  if (!history || !Array.isArray(history) || history.length === 0) return <p className="text-sm text-slate-400">Sem histórico.</p>;
  return (
    <div className="relative pl-6 border-l-2 border-indigo-100 space-y-8 my-4">
      {history.map((step, idx) => {
        const isLatest = idx === history.length - 1;
        const config = STATUS_CONFIG[step.status] || { label: String(step.status || 'Desconhecido') };
        return (
          <div key={idx} className={`relative transition-all duration-500 ${isLatest ? 'opacity-100 translate-x-0' : 'opacity-70'}`}>
            <div className={`absolute -left-[29px] top-0 w-5 h-5 rounded-full border-4 border-white shadow-md ${isLatest ? 'bg-indigo-600 scale-110' : 'bg-slate-300'}`}></div>
            <div className="bg-white p-3 rounded-lg border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <p className={`text-sm font-bold ${isLatest ? 'text-indigo-900' : 'text-slate-600'}`}>{config.label}</p>
                <span className="text-[10px] font-mono text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">{formatDate(step.date)}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{String(step.note || '')}</p>
              {step.actor && (
                <div className="mt-2 flex items-center gap-1 text-[10px] text-indigo-600 font-medium">
                  <User className="w-3 h-3" /> {String(step.actor)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- APP PRINCIPAL ---
export default function App() {
  const { user, userProfile, canAccess, loading: authLoading, authError, setAuthError, signIn, signOut } = useAuth();
  const toast = useToast();
  const [view, setView] = useState('dashboard');
  const [deliveries, setDeliveries] = useState([]);
  const [recipients, setRecipients] = useState([]);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('amc-log-sidebar-collapsed') === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('amc-log-sidebar-collapsed', sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);
  const [showNewDeliveryModal, setShowNewDeliveryModal] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(null);
  const [isInstantDelivery, setIsInstantDelivery] = useState(false);
  const [instantDeliveryPassword, setInstantDeliveryPassword] = useState('');
  const [securityModal, setSecurityModal] = useState({
    show: false,
    delivery: null,
    type: '',
    deliveryMode: SECURITY_DELIVERY_MODE.OWNER,
    receiverUid: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deleteModal, setDeleteModal] = useState({ show: false, delivery: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletionAuditLogs, setDeletionAuditLogs] = useState([]);
  const [isSubmittingDelivery, setIsSubmittingDelivery] = useState(false);
  const [isConfirmingSecurity, setIsConfirmingSecurity] = useState(false);
  const [movingIds, setMovingIds] = useState(() => new Set());
  const [flashIds, setFlashIds] = useState(() => new Set());
  const [securityCheckSuccess, setSecurityCheckSuccess] = useState(false);
  const [auditDetailModal, setAuditDetailModal] = useState({ show: false, log: null });
  const [isDeletingAuditLog, setIsDeletingAuditLog] = useState(false);
  const [newDelivery, setNewDelivery] = useState({ ownerUid: '', owner_username: '', description: '', initial_destination: 'default' });

  // Filtros da aba Auditoria
  const [auditSearch, setAuditSearch] = useState('');
  const [auditStatusFilter, setAuditStatusFilter] = useState('');
  const [auditDateFrom, setAuditDateFrom] = useState('');
  const [auditDateTo, setAuditDateTo] = useState('');
  const [auditActorFilter, setAuditActorFilter] = useState('');

  // --- HELPER FUNCTIONS ---
  const getMyDeliveries = () => {
    if (!userProfile || !user) return [];
    if (hasRole(userProfile, 'admin')) return deliveries;
    const myUid = user.uid;
    const myEmail = user.email?.toLowerCase?.() || '';
    let myDels = [];
    if (hasRole(userProfile, 'dono') || hasRole(userProfile, 'colaborador')) {
      const ownerDels = deliveries.filter(d => (d.ownerUid && d.ownerUid === myUid) || (d.owner_username && d.owner_username.toLowerCase() === myEmail));
      myDels = [...myDels, ...ownerDels];
    }
    if (hasRole(userProfile, 'portaria')) {
      myDels = [...myDels, ...deliveries.filter(portariaCanDeliverWithPin)];
    }
    if (hasRole(userProfile, 'recepcao') || hasRole(userProfile, 'recepção')) {
      myDels = [...myDels, ...deliveries.filter(d => d.status === 'at_reception' || d.status === 'transit_to_reception')];
    }
    return [...new Map(myDels.map(item => [item.id, item])).values()];
  };

  const getAuditLogs = () => {
    if (!deliveries) return [];
    return deliveries.flatMap(d => 
      (d.history || []).map(h => ({
        eventId: `${d.id}-${h.date}`,
        deliveryId: d.id,
        itemDescription: String(d.description || ''),
        owner: String(d.owner_name || ''),
        date: h.date,
        status: h.status,
        note: String(h.note || ''),
        actor: String(h.actor || 'Sistema')
      }))
    ).sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const closeSecurityModal = () => {
    setSecurityModal({
      show: false,
      delivery: null,
      type: '',
      deliveryMode: SECURITY_DELIVERY_MODE.OWNER,
      receiverUid: '',
    });
    setConfirmPassword('');
  };

  const alternateRecipients = useMemo(() => {
    const ownerUid = securityModal.delivery?.ownerUid;
    return recipients.filter(r => r.id !== ownerUid);
  }, [recipients, securityModal.delivery]);

  const selectedSecurityReceiver = useMemo(() => {
    return alternateRecipients.find(r => r.id === securityModal.receiverUid) || null;
  }, [alternateRecipients, securityModal.receiverUid]);

  const isThirdPartyDelivery = securityModal.deliveryMode === SECURITY_DELIVERY_MODE.THIRD_PARTY;
  const securityPinLabel = securityModal.type === 'owner'
    ? (isThirdPartyDelivery ? 'PIN de Quem Está Retirando' : 'PIN do Destinatário')
    : 'PIN do Recebedor';
  const securitySubtitle = securityModal.type === 'owner'
    ? (isThirdPartyDelivery
      ? 'Selecione quem está retirando e valide o PIN do colaborador.'
      : 'Destinatário original: digite o PIN para receber.')
    : 'Recepção: digite o PIN para receber.';
  const securityConfirmDisabled = securityModal.type === 'owner' && isThirdPartyDelivery && !selectedSecurityReceiver;

  // --- FIREBASE SYNC: deliveries (banco sistemas-amc) ---
  useEffect(() => {
    if (!user || !canAccess) return;
    const ref = getDeliveriesRef();
    const unsub = onSnapshot(ref, (snapshot) => {
      const delList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setDeliveries(delList.sort((a, b) => {
        const dateA = new Date(a.createdAt || (a.history?.[0]?.date || new Date()));
        const dateB = new Date(b.createdAt || (b.history?.[0]?.date || new Date()));
        return dateB - dateA;
      }));
    }, (err) => { console.error(err); });
    return () => unsub();
  }, [user, canAccess]);

  // --- Lista de destinatários (usuários com role colaborador/admin em sistema_amc_log). Requer regra no Firestore. ---
  useEffect(() => {
    if (!user || !canAccess) return;
    const load = async () => {
      try {
        const q = query(
          collection(db, 'users'),
          where(`roles.${SYSTEM_ID}`, 'in', ['colaborador', 'admin'])
        );
        const snap = await getDocs(q);
        setRecipients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.warn('Lista de destinatários não disponível (verifique regras Firestore):', err);
        setRecipients([]);
      }
    };
    load();
  }, [user, canAccess]);

  useEffect(() => {
    if (!user || !canAccess || !hasRole(userProfile, 'admin')) return;
    const load = async () => {
      try {
        const { logs } = await getDeletionAuditLogs();
        setDeletionAuditLogs(Array.isArray(logs) ? logs : []);
      } catch (err) {
        console.warn('Logs de exclusão não disponíveis:', err);
        setDeletionAuditLogs([]);
      }
    };
    load();
  }, [user, canAccess, userProfile]);

  const flashRow = useCallback((id) => {
    setFlashIds(prev => { const n = new Set(prev); n.add(id); return n; });
    setTimeout(() => {
      setFlashIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    }, 1250);
  }, []);

  // --- ACTIONS ---
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      await signIn(loginData.email.trim(), loginData.password);
    } catch {
      // authError já definido no AuthContext
    }
  };

  const handleNavigation = (newView) => {
    setView(newView);
    setSidebarOpen(false);
  };

  const exportToCSV = () => {
    if (deliveries.length === 0) { toast.warning({ title: 'Sem dados para exportar' }); return; }
    const headers = ["ID", "Data Recebimento", "Data Última Atualização", "Descrição", "Dono", "Status", "Local", "Responsável"];
    const rows = deliveries.map(d => {
      const dateCreated = formatDate(d.createdAt);
      let lastUpdate = '-';
      if (d.history && d.history.length > 0) {
          lastUpdate = formatDate(d.history[d.history.length - 1].date);
      }
      const statusLabel = STATUS_CONFIG[d.status]?.label || d.status;
      const clean = (text) => `"${(text || '').toString().replace(/"/g, '""')}"`;
      return [clean(d.id.slice(0, 8)), clean(dateCreated), clean(lastUpdate), clean(d.description), clean(d.owner_name), clean(statusLabel), clean(d.location), clean(d.current_holder)].join(";");
    });
    const csvContent = "\uFEFF" + [headers.join(";"), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `relatorio_amc_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success({ title: 'Relatório exportado', subtitle: 'O download começou em CSV.' });
  };

  const createDelivery = async (e) => {
    e.preventDefault();
    if (isSubmittingDelivery) return;
    const owner = recipients.find(r => r.id === newDelivery.ownerUid || (r.email && r.email.toLowerCase() === (newDelivery.owner_username || '').toLowerCase()));
    if (!owner) { toast.warning({ title: 'Selecione um destinatário', subtitle: 'Escolha um colaborador antes de confirmar o registro.' }); return; }
    setIsSubmittingDelivery(true);
    try {
      const now = getISODate();
      const creatorName = getDisplayName(userProfile);
      const ownerUid = owner.id;
      const owner_username = owner.email || owner.username || newDelivery.owner_username;
      const owner_name = getDisplayName(owner);
      let status = 'received_portaria';
      let note = '';
      let current_holder = 'portaria';
      let location = 'Na Portaria';
      let pendingOwnerHandoff = false;

      if (newDelivery.initial_destination === 'dono' && isInstantDelivery) {
        try {
          const result = await validateUserPin({ pin: instantDeliveryPassword, targetUid: ownerUid });
          if (!result?.valid) { toast.error({ title: 'Senha do dono incorreta', subtitle: 'A entrega não foi confirmada. Verifique o PIN com o destinatário.' }); return; }
        } catch (err) {
          toast.error({ title: 'Erro ao validar senha', subtitle: 'Tente novamente em instantes.' });
          return;
        }
        status = 'delivered';
        note = `Entregue diretamente na criação por ${creatorName} (Validado com Senha)`;
        current_holder = 'finished';
        location = 'Concluído';
        pendingOwnerHandoff = false;
      } else {
        const built = buildNewDeliveryState({
          initialDestination: newDelivery.initial_destination,
          creatorIsRecepcao: hasRole(userProfile, 'recepcao'),
          creatorName,
        });
        ({ status, current_holder, location, note, pendingOwnerHandoff } = built);
      }

      const ref = await addDoc(getDeliveriesRef(), {
        ownerUid,
        owner_username,
        owner_name,
        location,
        current_holder,
        status,
        pendingOwnerHandoff,
        description: newDelivery.description,
        createdAt: now,
        history: [{ status, date: now, note, actor: creatorName }],
      });
      flashRow(ref.id);
      const ownerLabel = owner_name ? ` para ${owner_name}` : '';
      if (status === 'delivered') {
        toast.success({ title: 'Entrega concluída', subtitle: `PIN validado${ownerLabel}. Registro criado e finalizado.` });
      } else if (status === 'transit_to_reception') {
        toast.info({ title: 'Encomenda enviada à Recepção', subtitle: `Aguardando confirmação de recebimento${ownerLabel}.` });
      } else if (status === 'at_reception') {
        toast.success({ title: 'Encomenda registrada na Recepção', subtitle: `Pronta para entrega${ownerLabel}.` });
      } else if (status === 'received_portaria') {
        toast.info({ title: 'Encomenda registrada na Portaria', subtitle: pendingOwnerHandoff ? `Concluir com Entrega c/ Senha${ownerLabel}.` : `Registro criado${ownerLabel}.` });
      }
      setShowNewDeliveryModal(false);
      setNewDelivery({ ownerUid: '', owner_username: '', description: '', initial_destination: 'default' });
      setIsInstantDelivery(false);
      setInstantDeliveryPassword('');
    } finally {
      setIsSubmittingDelivery(false);
    }
  };

  const handleSecurityCheck = (delivery, type) => {
    setSecurityModal({
      show: true,
      delivery,
      type,
      deliveryMode: SECURITY_DELIVERY_MODE.OWNER,
      receiverUid: '',
    });
    setConfirmPassword('');
  };

  const confirmSecurityCheck = async (e) => {
    e.preventDefault();
    if (isConfirmingSecurity) return;
    const targetUid = getSecurityValidationTargetUid({
      securityType: securityModal.type,
      deliveryMode: securityModal.deliveryMode,
      delivery: securityModal.delivery,
      receiverUid: securityModal.receiverUid,
      currentUserUid: user?.uid,
    });
    if (securityModal.type === 'owner' && isThirdPartyDelivery && !selectedSecurityReceiver) {
      toast.warning({ title: 'Selecione quem está retirando', subtitle: 'Informe o colaborador que retira em nome do destinatário.' });
      return;
    }
    if (securityModal.type === 'owner' && !targetUid) {
      toast.error({ title: 'Entrega sem destinatário vinculado', subtitle: 'Não é possível validar sem ownerUid. Contate o administrador.' });
      return;
    }
    setIsConfirmingSecurity(true);
    try {
      const result = await validateUserPin({ pin: confirmPassword, targetUid });
      if (!result?.valid) { toast.error({ title: 'PIN incorreto', subtitle: 'A entrega não foi autorizada.' }); return; }
      if (securityModal.type === 'owner') {
        await movePackage(securityModal.delivery, 'deliver_to_owner_secure', {
          deliveryMode: securityModal.deliveryMode,
          receiver: selectedSecurityReceiver,
        });
      } else if (securityModal.type === 'reception') {
        await movePackage(securityModal.delivery, 'handover_to_reception_secure');
      }
      setSecurityCheckSuccess(true);
      setTimeout(() => {
        setSecurityCheckSuccess(false);
        closeSecurityModal();
      }, 700);
    } catch (err) {
      console.error(err);
      toast.error({ title: 'Erro ao validar PIN', subtitle: 'Tente novamente em instantes.' });
    } finally {
      setIsConfirmingSecurity(false);
    }
  };

  const movePackage = async (del, action, options = {}) => {
    setMovingIds(prev => { const n = new Set(prev); n.add(del.id); return n; });
    try {
      const now = getISODate();
      const actorName = getDisplayName(userProfile);
      let newStatus = del.status, newHolder = del.current_holder, newLocation = del.location, note = '';
      let extraUpdates = {};

      if (action === 'send_to_reception') { newStatus = 'transit_to_reception'; newHolder = 'recepcao'; newLocation = 'A caminho da Recepção'; note = `Enviado para Recepção por ${actorName}`; }
      else if (action === 'handover_to_reception_secure') { newStatus = 'at_reception'; newHolder = 'recepcao'; newLocation = 'Na Recepção'; note = `Entrega para Recepção confirmada com senha presencial`; }
      else if (action === 'confirm_reception_arrival') { newStatus = 'at_reception'; newHolder = 'recepcao'; newLocation = 'Na Recepção'; note = `Recebimento confirmado por ${actorName}`; }
      else if (action === 'deliver_to_owner_secure') {
        const receipt = buildSecureDeliveryReceipt({
          delivery: del,
          deliveryMode: options.deliveryMode,
          receiver: options.receiver,
        });
        newStatus = 'delivered';
        newHolder = 'finished';
        newLocation = 'Concluído';
        note = receipt.note;
        extraUpdates = {
          receivedByUid: receipt.receivedByUid,
          receivedByName: receipt.receivedByName,
          receivedByUsername: receipt.receivedByUsername,
          receivedByType: receipt.receivedByType,
          pendingOwnerHandoff: false,
        };
      }

      await updateDoc(getDeliveryDoc(del.id), {
        status: newStatus,
        current_holder: newHolder,
        location: newLocation,
        ...extraUpdates,
        history: [...(del.history || []), { status: newStatus, date: now, note, actor: actorName }],
      });
      flashRow(del.id);
      if (action === 'send_to_reception') {
        toast.info({ title: 'Enviado à Recepção', subtitle: 'Aguardando confirmação.' });
      } else if (action === 'handover_to_reception_secure') {
        toast.success({ title: 'Recepção recebeu com PIN', subtitle: 'Encomenda agora está na Recepção.' });
      } else if (action === 'confirm_reception_arrival') {
        toast.success({ title: 'Recebimento confirmado', subtitle: 'Encomenda agora está na Recepção.' });
      } else if (action === 'deliver_to_owner_secure') {
        const receiverName = extraUpdates.receivedByName || String(del.owner_name || '');
        toast.success({ title: 'Entrega concluída', subtitle: receiverName ? `Recebida por ${receiverName}.` : 'PIN validado com sucesso.' });
      }
    } finally {
      setMovingIds(prev => { const n = new Set(prev); n.delete(del.id); return n; });
    }
  };

  const handleDeleteDelivery = async () => {
    if (!deleteModal.delivery) return;
    setIsDeleting(true);
    try {
      const subtitle = deleteModal.delivery?.description
        ? String(deleteModal.delivery.description)
        : '';
      await deleteDelivery(deleteModal.delivery.id);
      setDeleteModal({ show: false, delivery: null });
      toast.neutral({ title: 'Mercadoria excluída permanentemente.', subtitle });
      if (hasRole(userProfile, 'admin')) {
        getDeletionAuditLogs()
          .then(({ logs }) => setDeletionAuditLogs(Array.isArray(logs) ? logs : []))
          .catch(() => {});
      }
    } catch (err) {
      console.error('Erro ao excluir entrega:', err);
      const code = err?.code || '';
      if (code === 'functions/not-found') {
        toast.warning({ title: 'Entrega não encontrada', subtitle: 'Ela pode já ter sido excluída.' });
        setDeleteModal({ show: false, delivery: null });
      } else if (code === 'functions/permission-denied') {
        toast.error({ title: 'Sem permissão para excluir', subtitle: 'Apenas administradores podem excluir entregas.' });
      } else {
        toast.error({ title: 'Erro ao excluir entrega', subtitle: 'Tente novamente em instantes.' });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAuditLog = async (auditLogId) => {
    if (!isGlobalAdmin(userProfile)) return;
    setIsDeletingAuditLog(true);
    try {
      await deleteAuditLog(auditLogId);
      setAuditDetailModal({ show: false, log: null });
      setDeletionAuditLogs(prev => prev.filter(l => l.id !== auditLogId));
      toast.neutral({ title: 'Log de auditoria excluído', subtitle: 'O registro foi removido permanentemente.' });
    } catch (err) {
      console.error('Erro ao excluir log de auditoria:', err);
      const code = err?.code || '';
      if (code === 'functions/permission-denied') {
        toast.error({ title: 'Sem permissão', subtitle: 'Você não pode excluir logs de auditoria.' });
      } else {
        toast.error({ title: 'Erro ao excluir log', subtitle: 'Tente novamente em instantes.' });
      }
    } finally {
      setIsDeletingAuditLog(false);
    }
  };

  const visibleDeliveries = getMyDeliveries();
  const auditLogs = getAuditLogs();

  const filteredAuditLogs = useMemo(() => {
    let list = [...(auditLogs || [])];
    const q = (auditSearch || '').trim().toLowerCase();
    if (q) {
      list = list.filter(log =>
        (log.actor || '').toLowerCase().includes(q) ||
        (log.note || '').toLowerCase().includes(q) ||
        (log.itemDescription || '').toLowerCase().includes(q) ||
        (log.owner || '').toLowerCase().includes(q)
      );
    }
    if (auditStatusFilter) list = list.filter(log => log.status === auditStatusFilter);
    if (auditActorFilter) list = list.filter(log => (log.actor || '').trim() === auditActorFilter);
    if (auditDateFrom) {
      const from = new Date(auditDateFrom);
      from.setHours(0, 0, 0, 0);
      list = list.filter(log => new Date(log.date) >= from);
    }
    if (auditDateTo) {
      const to = new Date(auditDateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter(log => new Date(log.date) <= to);
    }
    return list;
  }, [auditLogs, auditSearch, auditStatusFilter, auditDateFrom, auditDateTo, auditActorFilter]);

  const auditFilterOptions = useMemo(() => {
    const statuses = [...new Set((auditLogs || []).map(l => l.status).filter(Boolean))].sort();
    const actors = [...new Set((auditLogs || []).map(l => (l.actor || '').trim()).filter(Boolean))].sort();
    return { statuses, actors };
  }, [auditLogs]);

  const exportAuditToCSV = () => {
    if (filteredAuditLogs.length === 0) { toast.warning({ title: 'Nenhum registro para exportar', subtitle: 'Ajuste os filtros e tente novamente.' }); return; }
    const headers = ['Data', 'Responsável', 'Ação', 'Item', 'Destinatário', 'Status'];
    const clean = (text) => `"${(text || '').toString().replace(/"/g, '""')}"`;
    const statusLabel = (s) => STATUS_CONFIG[s]?.label || s;
    const rows = filteredAuditLogs.map(log => [
      clean(formatDate(log.date)),
      clean(log.actor),
      clean(log.note),
      clean(log.itemDescription),
      clean(log.owner),
      clean(statusLabel(log.status)),
    ].join(';'));
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `auditoria_amc_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success({ title: 'Relatório exportado', subtitle: 'O download começou em CSV.' });
  };

  const stats = useMemo(() => {
    let relevantDeliveries = deliveries;
    if (userProfile && user && hasRole(userProfile, 'dono') && !hasRole(userProfile, 'admin')) {
      relevantDeliveries = deliveries.filter(d => (d.ownerUid && d.ownerUid === user.uid) || (d.owner_username && d.owner_username.toLowerCase() === (user.email || '').toLowerCase()));
    }
    const total = relevantDeliveries.length;
    const portaria = relevantDeliveries.filter(d => d.status === 'received_portaria').length;
    const transit = relevantDeliveries.filter(d => d.status === 'transit_to_reception').length;
    const recepcao = relevantDeliveries.filter(d => d.status === 'at_reception').length;
    const waiting = relevantDeliveries.filter(d => d.status === 'waiting_confirmation').length;
    const delivered = relevantDeliveries.filter(d => d.status === 'delivered').length;
    // Departamento: desativado por enquanto (futuramente reativar)
    // const deptCount = {};
    // relevantDeliveries.forEach(d => {
    //   const rec = recipients.find(r => r.id === d.ownerUid || (r.email && r.email.toLowerCase() === (d.owner_username || '').toLowerCase()));
    //   const dept = rec?.department || 'Outros';
    //   deptCount[dept] = (deptCount[dept] || 0) + 1;
    // });
    // const deptChart = Object.entries(deptCount).sort((a, b) => b[1] - a[1]).slice(0, 5);
    const deptChart = [];
    const pendingItemsCount = portaria + transit + recepcao + waiting;
    const hasPending = pendingItemsCount > 0;
    return { total, portaria, transit, recepcao, waiting, delivered, deptChart, pendingItemsCount, hasPending };
  }, [deliveries, user, userProfile]);

  // --- RENDER ---
  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white animate-pulse font-sans"><Wifi className="w-6 h-6 mr-2"/> Iniciando AMC Log...</div>;

  if (!user) {
    return (
      <>
        <style>{customStyles}</style>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-900 flex items-center justify-center p-4 font-sans">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up">
            <div className="bg-slate-900 p-6 sm:p-10 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full bg-indigo-600 opacity-10"></div>
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-indigo-500/30 overflow-hidden bg-white/10">
                <img src={`${process.env.PUBLIC_URL || ''}/app-icon.png`} alt="AMC Log" className="w-14 h-14 object-contain" />
              </div>
              <h1 className="text-3xl font-bold text-white tracking-tight">AMC Log</h1>
              <p className="text-indigo-200 text-sm mt-2 font-medium">Gestão Inteligente de Entregas</p>
            </div>
            <form onSubmit={handleLogin} className="p-8 space-y-5">
              {authError && <div className="p-4 bg-red-50 text-red-600 rounded-xl text-sm flex items-center gap-3 border border-red-100"><Ban className="w-5 h-5 flex-shrink-0"/>{authError}</div>}
              <div className="space-y-4">
                <div className="relative group">
                    <Mail className="absolute left-4 top-3.5 w-5 h-5 text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                    <input type="email" placeholder="E-mail Corporativo" className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-indigo-600 focus:ring-4 focus:ring-indigo-50 transition-all bg-slate-50 focus:bg-white" value={loginData.email} onChange={e => setLoginData({ ...loginData, email: e.target.value })} />
                </div>
                <div className="relative group">
                    <Lock className="absolute left-4 top-3.5 w-5 h-5 text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                    <input type="password" placeholder="Senha" className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-indigo-600 focus:ring-4 focus:ring-indigo-50 transition-all bg-slate-50 focus:bg-white" value={loginData.password} onChange={e => setLoginData({ ...loginData, password: e.target.value })} />
                </div>
              </div>
              <button type="submit" className="w-full bg-indigo-600 text-white py-3.5 rounded-xl font-bold hover:bg-indigo-700 active:scale-95 transition-all shadow-lg shadow-indigo-200">Acessar Sistema</button>
            </form>
          </div>
        </div>
      </>
    );
  }

  if (!canAccess) {
    return (
      <>
        <style>{customStyles}</style>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-900 flex items-center justify-center p-4 font-sans">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up p-8 text-center">
            <h1 className="text-xl font-bold text-slate-800">Sem acesso ao AMC Log</h1>
            <p className="text-slate-600 mt-2">Sua conta não tem permissão para este sistema. Entre em contato com o administrador.</p>
            <button type="button" onClick={signOut} className="mt-6 w-full flex items-center justify-center gap-2 bg-slate-200 hover:bg-slate-300 text-slate-800 py-3 rounded-xl font-bold transition-colors">
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{customStyles}</style>
      <div className="h-screen max-h-screen min-h-0 bg-slate-50 font-sans flex items-start overflow-hidden">
        {sidebarOpen && <div className="fixed inset-0 bg-black/60 z-20 md:hidden backdrop-blur-sm" onClick={() => setSidebarOpen(false)}></div>}

        <aside
          className={`fixed md:sticky md:top-0 z-30 sidebar-gradient text-white flex flex-col shadow-2xl shrink-0 self-start h-screen max-h-screen transition-[width,transform] duration-300 ease-out motion-safe:duration-300 ${
            sidebarCollapsed ? 'md:w-[4.25rem]' : 'md:w-72'
          } w-72 max-w-[85vw] ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        >
          {/* Topo: marca + controles (referência ALMOXPRO: logo isolado no header) */}
          <div className={`flex items-center border-b border-white/10 shrink-0 gap-3 ${sidebarCollapsed ? 'md:p-3 md:justify-center md:flex-col md:gap-2' : 'px-5 py-5 justify-between'}`}>
            <div className={`flex items-center gap-3 min-w-0 flex-1 ${sidebarCollapsed ? 'md:justify-center md:flex-none' : ''}`}>
              <img src={`${process.env.PUBLIC_URL || ''}/app-icon.png`} alt="" className="w-11 h-11 object-contain shrink-0 rounded-xl ring-1 ring-white/10 shadow-lg" />
              <div className={sidebarCollapsed ? 'md:hidden' : ''}>
                <span className="font-bold text-lg tracking-tight text-white leading-tight block whitespace-nowrap">
                  AMC <span className="text-indigo-300">Log</span>
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                className="hidden md:flex p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-colors motion-safe:duration-200"
                onClick={() => setSidebarCollapsed((c) => !c)}
                aria-expanded={!sidebarCollapsed}
                aria-label={sidebarCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
                title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
              >
                {sidebarCollapsed ? <ChevronsRight className="w-5 h-5" /> : <ChevronsLeft className="w-5 h-5" />}
              </button>
              <button type="button" className="md:hidden text-slate-400 hover:text-white p-1 rounded-lg" onClick={() => setSidebarOpen(false)} aria-label="Fechar menu"><X className="w-6 h-6" /></button>
            </div>
          </div>

          {/* Meio: navegação rolável (ícone + label em caixa, item ativo com brilho índigo) */}
          <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-1.5" aria-label="Navegação principal">
            <button
              type="button"
              title="Dashboard"
              onClick={() => handleNavigation('dashboard')}
              className={`w-full flex items-center gap-3 rounded-2xl py-2.5 text-left transition-colors motion-safe:duration-200 group ${sidebarCollapsed ? 'md:justify-center md:px-2' : 'px-3'} ${
                view === 'dashboard'
                  ? 'bg-indigo-600 text-white sidebar-nav-active'
                  : 'text-slate-400 hover:bg-white/10 hover:text-white'
              }`}
            >
              <BarChart3 className={`w-[1.15rem] h-[1.15rem] shrink-0 ${view === 'dashboard' ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} />
              <span className={`text-[11px] font-bold uppercase tracking-[0.14em] truncate ${sidebarCollapsed ? 'md:hidden' : ''}`}>Dashboard</span>
            </button>
            <button
              type="button"
              title={hasRole(userProfile, 'dono') && !hasRole(userProfile, 'portaria') ? 'Minhas Encomendas' : 'Mercadorias'}
              onClick={() => handleNavigation('deliveries')}
              className={`w-full flex items-center gap-3 rounded-2xl py-2.5 text-left transition-colors motion-safe:duration-200 group ${sidebarCollapsed ? 'md:justify-center md:px-2' : 'px-3'} ${
                view === 'deliveries'
                  ? 'bg-indigo-600 text-white sidebar-nav-active'
                  : 'text-slate-400 hover:bg-white/10 hover:text-white'
              }`}
            >
              <Package className={`w-[1.15rem] h-[1.15rem] shrink-0 ${view === 'deliveries' ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} />
              <span className={`text-[11px] font-bold uppercase tracking-[0.14em] truncate flex-1 text-left ${sidebarCollapsed ? 'md:hidden' : ''}`}>
                {hasRole(userProfile, 'dono') && !hasRole(userProfile, 'portaria') ? 'Minhas encomendas' : 'Mercadorias'}
              </span>
              {!sidebarCollapsed &&
                (hasRole(userProfile, 'dono') && stats.waiting > 0 ? (
                  <span className="ml-auto min-w-[1.25rem] h-5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full animate-pulse shrink-0 px-1.5">
                    {stats.waiting}
                  </span>
                ) : (
                  visibleDeliveries.length > 0 && (
                    <span className="ml-auto min-w-[1.25rem] h-5 flex items-center justify-center bg-indigo-500 text-white text-[10px] font-bold rounded-full shrink-0 px-1.5">
                      {visibleDeliveries.length}
                    </span>
                  )
                ))}
            </button>

            {hasRole(userProfile, 'admin') && (
              <>
                <div className={`my-3 border-t border-white/10 ${sidebarCollapsed ? 'md:mx-1' : ''}`} role="presentation" />
                <div className={`px-3 pt-1 pb-1 text-[10px] font-bold text-slate-500 uppercase tracking-[0.18em] ${sidebarCollapsed ? 'md:hidden' : ''}`}>
                  Administração
                </div>
                <button
                  type="button"
                  title="Usuários"
                  onClick={() => handleNavigation('users')}
                  className={`w-full flex items-center gap-3 rounded-2xl py-2.5 text-left transition-colors motion-safe:duration-200 group ${sidebarCollapsed ? 'md:justify-center md:px-2' : 'px-3'} ${
                    view === 'users' ? 'bg-indigo-600 text-white sidebar-nav-active' : 'text-slate-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Users className={`w-[1.15rem] h-[1.15rem] shrink-0 ${view === 'users' ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} />
                  <span className={`text-[11px] font-bold uppercase tracking-[0.14em] truncate ${sidebarCollapsed ? 'md:hidden' : ''}`}>Usuários</span>
                </button>
                <button
                  type="button"
                  title="Relatórios"
                  onClick={() => handleNavigation('reports')}
                  className={`w-full flex items-center gap-3 rounded-2xl py-2.5 text-left transition-colors motion-safe:duration-200 group ${sidebarCollapsed ? 'md:justify-center md:px-2' : 'px-3'} ${
                    view === 'reports' ? 'bg-indigo-600 text-white sidebar-nav-active' : 'text-slate-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <FileSpreadsheet className={`w-[1.15rem] h-[1.15rem] shrink-0 ${view === 'reports' ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} />
                  <span className={`text-[11px] font-bold uppercase tracking-[0.14em] truncate ${sidebarCollapsed ? 'md:hidden' : ''}`}>Relatórios</span>
                </button>
                <button
                  type="button"
                  title="Auditoria"
                  onClick={() => handleNavigation('audit')}
                  className={`w-full flex items-center gap-3 rounded-2xl py-2.5 text-left transition-colors motion-safe:duration-200 group ${sidebarCollapsed ? 'md:justify-center md:px-2' : 'px-3'} ${
                    view === 'audit' ? 'bg-indigo-600 text-white sidebar-nav-active' : 'text-slate-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Shield className={`w-[1.15rem] h-[1.15rem] shrink-0 ${view === 'audit' ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} />
                  <span className={`text-[11px] font-bold uppercase tracking-[0.14em] truncate ${sidebarCollapsed ? 'md:hidden' : ''}`}>Auditoria</span>
                </button>
              </>
            )}
          </nav>

          {/* Rodapé: card do usuário + sair (referência: bloco inferior estável) */}
          <div className={`shrink-0 border-t border-white/10 p-3 ${sidebarCollapsed ? 'md:px-2' : ''}`}>
            <div
              className={`rounded-2xl border border-white/10 bg-slate-950/40 backdrop-blur-sm p-3 shadow-inner ${sidebarCollapsed ? 'md:p-2 md:flex md:flex-col md:items-center' : ''}`}
            >
              <div className={`flex items-center gap-3 ${sidebarCollapsed ? 'md:flex-col md:gap-2' : ''}`}>
                <div
                  className="w-11 h-11 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold text-base shrink-0 ring-2 ring-indigo-500/40"
                  title={getDisplayName(userProfile)}
                >
                  {String(getDisplayName(userProfile))[0]}
                </div>
                <div className={`overflow-hidden min-w-0 flex-1 ${sidebarCollapsed ? 'md:hidden' : ''}`}>
                  <p className="font-bold text-white text-sm truncate leading-tight">{getDisplayName(userProfile)}</p>
                  <p className="text-[11px] text-indigo-300/90 font-semibold truncate mt-0.5">{getRoleLabel(userProfile)}</p>
                </div>
              </div>
              <button
                type="button"
                title="Encerrar sessão"
                onClick={() => signOut()}
                className={`mt-3 group w-full flex items-center justify-center gap-2 rounded-xl border border-white/5 bg-slate-900/60 py-2.5 text-sm font-bold uppercase tracking-wide text-rose-300/95 transition-colors motion-safe:duration-200 hover:border-red-300/80 hover:bg-red-300 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60 ${sidebarCollapsed ? 'md:hidden' : ''}`}
              >
                <LogOut className="w-4 h-4 shrink-0 opacity-90 group-hover:opacity-100 group-hover:text-white" aria-hidden />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </aside>

        <main className="flex-1 min-h-0 h-full overflow-y-auto bg-slate-50 relative">
          <header className="bg-white/80 backdrop-blur-md sticky top-0 z-20 border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 shadow-sm flex-wrap sm:flex-nowrap">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <button type="button" className="md:hidden p-2 -ml-2 text-slate-600 hover:bg-slate-100 rounded-lg flex-shrink-0" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Abrir menu"><Menu className="w-6 h-6"/></button>
              {sidebarCollapsed && (
                <button
                  type="button"
                  className="hidden md:flex p-2 text-slate-600 hover:bg-slate-100 rounded-lg flex-shrink-0"
                  onClick={() => setSidebarCollapsed(false)}
                  aria-label="Expandir menu lateral"
                  title="Expandir menu"
                >
                  <ChevronsRight className="w-5 h-5" />
                </button>
              )}
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-bold text-slate-800 tracking-tight flex items-center gap-2 truncate">
                  {view === 'dashboard' && <><Activity className="w-5 h-5 text-indigo-600"/> Visão Geral</>}
                  {view === 'deliveries' && <><Package className="w-5 h-5 text-indigo-600"/> Controle de Mercadorias</>}
                  {view === 'users' && <><Users className="w-5 h-5 text-indigo-600"/> Gestão de Acessos</>}
                  {view === 'reports' && <><FileText className="w-5 h-5 text-indigo-600"/> Central de Relatórios</>}
                  {view === 'audit' && <><Shield className="w-5 h-5 text-indigo-600"/> Logs de Auditoria</>}
                </h2>
              </div>
            </div>
          </header>

          <div className="p-4 sm:p-6 max-w-7xl mx-auto animate-fade-in-up">
            
            {/* --- ALERT BANNER (DONO) --- */}
            {view === 'dashboard' && hasRole(userProfile, 'dono') && stats.hasPending && (
                <div className={`mb-6 sm:mb-8 border p-4 sm:p-6 rounded-2xl shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 relative overflow-hidden animate-fade-in-up ${stats.waiting > 0 ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-100' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100'}`}>
                    <div className={`absolute top-0 right-0 p-4 opacity-10 ${stats.waiting > 0 ? 'text-orange-500' : 'text-blue-500'}`}><Bell className="w-24 h-24" /></div>
                    <div className="flex items-center gap-4 relative z-10 flex-1 min-w-0">
                        <div className={`p-3 rounded-full animate-bounce ${stats.waiting > 0 ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}><Bell className="w-6 h-6" /></div>
                        <div>
                            <h3 className={`font-bold text-lg ${stats.waiting > 0 ? 'text-orange-900' : 'text-blue-900'}`}>Atualização de Encomendas</h3>
                            <div className={`text-sm space-y-1 ${stats.waiting > 0 ? 'text-orange-700' : 'text-blue-700'}`}>
                                {stats.portaria > 0 && <p>• {stats.portaria} item(ns) chegou na Portaria.</p>}
                                {stats.transit > 0 && <p>• {stats.transit} item(ns) em trânsito interno.</p>}
                                {stats.recepcao > 0 && <p>• {stats.recepcao} item(ns) aguardando na Recepção.</p>}
                                {stats.waiting > 0 && <p className="font-bold">• {stats.waiting} item(ns) aguardando sua confirmação final.</p>}
                            </div>
                        </div>
                    </div>
                    <button onClick={() => setView('deliveries')} className={`relative z-10 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg transition-all active:scale-95 ${stats.waiting > 0 ? 'bg-orange-600 hover:bg-orange-700 shadow-orange-200' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'}`}>
                        Verificar Agora
                    </button>
                </div>
            )}

            {/* --- DASHBOARD VIEW --- */}
            {view === 'dashboard' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  {[
                    { label: 'Na Portaria', val: stats.portaria, color: 'border-slate-500 text-slate-700', bg: 'bg-white', icon: Box },
                    { label: 'Em Trânsito', val: stats.transit, color: 'border-amber-500 text-amber-700', bg: 'bg-amber-50', icon: Truck },
                    { label: 'Na Recepção', val: stats.recepcao, color: 'border-purple-500 text-purple-700', bg: 'bg-white', icon: Building },
                    { label: 'Aguardando', val: stats.waiting, color: 'border-orange-500 text-orange-700', bg: 'bg-orange-50', icon: Clock },
                    { label: 'Entregues', val: stats.delivered, color: 'border-emerald-500 text-emerald-700', bg: 'bg-white', icon: CheckCircle },
                  ].map((card, i) => (
                    <div key={i} className={`p-5 rounded-2xl shadow-sm border-b-4 ${card.color} ${card.bg} flex flex-col justify-between hover:-translate-y-1 transition-transform duration-300`}>
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider opacity-70">{card.label}</span>
                        <card.icon className="w-5 h-5 opacity-80" />
                      </div>
                      <div className="text-3xl font-extrabold">{card.val}</div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Volume por Departamento: desativado por enquanto (futuramente reativar) */}
                    {/* <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-bold text-slate-800 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-indigo-600"/> Volume por Departamento</h3>
                            <span className="text-xs text-slate-400">{hasRole(userProfile, 'dono') && !hasRole(userProfile, 'admin') ? 'Suas Encomendas' : 'Top 5 Destinos'}</span>
                        </div>
                        <div className="space-y-4">
                            {stats.deptChart.length > 0 ? stats.deptChart.map(([dept, count], i) => (
                                <div key={i} className="relative">
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="font-medium text-slate-700">{dept || 'Não informado'}</span>
                                        <span className="font-bold text-slate-900">{count}</span>
                                    </div>
                                    <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                                        <div className="bg-indigo-600 h-2.5 rounded-full transition-all duration-1000 ease-out" style={{ width: `${(count / stats.total) * 100}%` }}></div>
                                    </div>
                                </div>
                            )) : <p className="text-slate-400 text-sm text-center py-4">Sem dados suficientes para gráfico.</p>}
                        </div>
                    </div> */}

                    <div className="lg:col-span-3 bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between">
                        <div>
                            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-600"/> Eficiência</h3>
                            <div className="space-y-4">
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <p className="text-xs text-slate-500 uppercase font-bold mb-1">Total Movimentado</p>
                                    <p className="text-2xl font-bold text-slate-800">{stats.total} <span className="text-sm font-normal text-slate-400">itens</span></p>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <p className="text-xs text-slate-500 uppercase font-bold mb-1">{hasRole(userProfile, 'dono') && !hasRole(userProfile, 'admin') ? 'Seus Pedidos Ativos' : 'Usuários Ativos'}</p>
                                    <p className="text-2xl font-bold text-slate-800">
                                        {hasRole(userProfile, 'dono') && !hasRole(userProfile, 'admin') ? (stats.total - stats.delivered) : recipients.length} 
                                        <span className="text-sm font-normal text-slate-400"> {hasRole(userProfile, 'dono') && !hasRole(userProfile, 'admin') ? 'pendentes' : 'colaboradores'}</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {(!hasRole(userProfile, 'dono') || hasRole(userProfile, 'admin')) && (
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                        <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Timer className="w-5 h-5 text-amber-600"/> Últimas Atividades</h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-bold">
                                    <tr><th className="p-3 rounded-l-lg">Horário</th><th className="p-3">Ação</th><th className="p-3">Usuário</th><th className="p-3 rounded-r-lg">Status</th></tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {auditLogs.slice(0, 5).map((log, i) => (
                                        <tr key={i} className="hover:bg-slate-50/50">
                                            <td className="p-3 font-mono text-xs text-slate-500">{formatDate(log.date)}</td>
                                            <td className="p-3 font-medium text-slate-700">{log.note}</td>
                                            <td className="p-3 text-slate-600">{log.actor}</td>
                                            <td className="p-3"><span className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-600">{STATUS_CONFIG[log.status]?.label}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
              </div>
            )}

            {/* --- DELIVERIES VIEW --- */}
            {view === 'deliveries' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                   <div className="relative w-full sm:w-96 group">
                      <Search className="absolute left-3 top-2.5 w-5 h-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors"/>
                      <input type="text" placeholder="Buscar encomenda..." className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all shadow-sm" />
                   </div>
                   {(hasRole(userProfile, 'portaria') || hasRole(userProfile, 'recepcao')) && (
                      <button onClick={() => setShowNewDeliveryModal(true)} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-indigo-200 active:scale-95 transition-all">
                        <Plus className="w-5 h-5" /> {hasRole(userProfile, 'recepcao') ? 'Receber na Recepção' : 'Nova Chegada'}
                      </button>
                   )}
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-slate-50/50 border-b border-slate-200 text-slate-500 text-xs uppercase font-bold tracking-wider">
                        <tr>
                          <th className="p-3 sm:p-5">Data Entrada</th>
                          <th className="p-3 sm:p-5">Descrição</th>
                          <th className="p-3 sm:p-5">Destinatário</th>
                          <th className="p-3 sm:p-5">Status</th>
                          <th className="p-3 sm:p-5 text-center">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-sm">
                        {visibleDeliveries.map(d => (
                          <tr key={d.id} className={`hover:bg-slate-50 transition-colors group ${flashIds.has(d.id) ? 'animate-row-flash' : ''}`}>
                            <td className="p-3 sm:p-5 font-mono text-xs text-slate-500">
                                <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" />{formatDate(d.createdAt)}</div>
                            </td>
                            <td className="p-3 sm:p-5 font-bold text-slate-700 text-sm sm:text-base max-w-[120px] sm:max-w-none truncate sm:overflow-visible sm:whitespace-normal" title={String(d.description)}>{String(d.description)}</td>
                            <td className="p-3 sm:p-5">
                                <div className="flex items-center gap-2 min-w-0">
                                    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-xs flex-shrink-0">{String(d.owner_name)[0]}</div>
                                    <div className="flex flex-col min-w-0">
                                        <span className="font-semibold text-slate-700 truncate">{String(d.owner_name)}</span>
                                        <span className="text-[10px] text-slate-400 truncate">{String(d.owner_username)}</span>
                                    </div>
                                </div>
                            </td>
                            <td className="p-3 sm:p-5">
                              <StatusBadge status={d.status} />
                              {d.pendingOwnerHandoff && (
                                <p className="text-[10px] text-orange-600 mt-1 font-medium max-w-[14rem]">
                                  Direto ao dono — conclua com Entrega c/ Senha (PIN)
                                </p>
                              )}
                            </td>
                            <td className="p-3 sm:p-5">
                              <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 opacity-100 sm:opacity-80 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => setShowTimelineModal(d.id)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Rastrear"><ClipboardList className="w-5 h-5"/></button>
                                
                                {hasRole(userProfile, 'admin') && (
                                    <button type="button" onClick={() => setDeleteModal({ show: true, delivery: d })} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Excluir entrega"><Trash2 className="w-5 h-5"/></button>
                                )}

                                {hasRole(userProfile, 'portaria') && portariaCanDeliverWithPin(d) && (
                                    <button type="button" onClick={() => handleSecurityCheck(d, 'owner')} className="px-3 py-1.5 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors border border-indigo-200"><Key className="w-3.5 h-3.5"/> Entrega c/ Senha</button>
                                )}

                                {hasRole(userProfile, 'recepcao') && (
                                    <>
                                        {d.status === 'transit_to_reception' && (
                                          <button
                                            type="button"
                                            disabled={movingIds.has(d.id)}
                                            onClick={() => movePackage(d, 'confirm_reception_arrival')}
                                            className={`px-4 py-2 rounded-lg shadow-md hover:shadow-lg text-xs font-bold flex items-center gap-2 transition-all ${movingIds.has(d.id) ? 'bg-emerald-400 cursor-not-allowed text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white animate-bounce'}`}
                                          >
                                            {movingIds.has(d.id) ? (
                                              <><span className="w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden /> Confirmando...</>
                                            ) : (
                                              <><ClipboardCheck className="w-4 h-4"/> Confirmar Recebimento</>
                                            )}
                                          </button>
                                        )}
                                        {d.status === 'at_reception' && (<button type="button" onClick={() => handleSecurityCheck(d, 'owner')} className="px-3 py-1.5 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors border border-indigo-200"><Key className="w-3.5 h-3.5"/> Entrega c/ Senha</button>)}
                                    </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {visibleDeliveries.length === 0 && (
                            <tr><td colSpan="5" className="p-12 text-center text-slate-400 flex flex-col items-center justify-center"><Package className="w-12 h-12 mb-3 opacity-20"/>Nenhuma mercadoria encontrada.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* --- REPORTS VIEW --- */}
            {view === 'reports' && hasRole(userProfile, 'admin') && (
               <div className="space-y-6">
                   <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 p-4 sm:p-8 rounded-2xl flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 shadow-sm">
                       <div className="flex items-start gap-4 min-w-0">
                           <div className="p-3 bg-white rounded-xl shadow-sm flex-shrink-0"><FileSpreadsheet className="w-8 h-8 text-emerald-600" /></div>
                           <div className="min-w-0">
                               <h3 className="font-bold text-emerald-900 text-lg sm:text-xl">Exportação de Dados</h3>
                               <p className="text-emerald-700 mt-1 max-w-lg text-sm sm:text-base">Baixe o relatório completo em CSV contendo histórico, tempos de entrega e responsáveis para auditoria externa.</p>
                           </div>
                       </div>
                       <button onClick={exportToCSV} className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-emerald-200 transition-all hover:-translate-y-0.5 active:scale-95 flex-shrink-0">
                           <Download className="w-5 h-5" /> Baixar Planilha
                       </button>
                   </div>
               </div>
            )}

            {/* --- AUDIT & USERS Views --- */}
            {view === 'audit' && hasRole(userProfile, 'admin') && (
               <div className="space-y-4">
                   <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                       <div className="p-4 sm:p-6 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                           <h3 className="font-bold text-slate-800 flex items-center gap-2"><Shield className="w-5 h-5 text-indigo-600"/> Auditoria Completa</h3>
                           <div className="flex flex-wrap items-center gap-2">
                               <span className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-1 rounded">{filteredAuditLogs.length} de {auditLogs.length} eventos</span>
                               <button type="button" onClick={exportAuditToCSV} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl font-bold text-sm shadow-sm transition-colors">
                                   <Download className="w-4 h-4" /> Exportar CSV
                               </button>
                           </div>
                       </div>
                       <div className="p-4 sm:p-6 border-b border-slate-100 space-y-4">
                           <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-stretch sm:items-center">
                               <div className="relative flex-1 min-w-[200px]">
                                   <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                   <input type="text" placeholder="Buscar por responsável, ação, item..." className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" value={auditSearch} onChange={e => setAuditSearch(e.target.value)} />
                               </div>
                               <select className="px-4 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white" value={auditStatusFilter} onChange={e => setAuditStatusFilter(e.target.value)}>
                                   <option value="">Todos os status</option>
                                   {auditFilterOptions.statuses.map(s => (<option key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</option>))}
                               </select>
                               <select className="px-4 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white" value={auditActorFilter} onChange={e => setAuditActorFilter(e.target.value)}>
                                   <option value="">Todos os responsáveis</option>
                                   {auditFilterOptions.actors.map(a => (<option key={a} value={a}>{a}</option>))}
                               </select>
                               <div className="flex flex-wrap items-center gap-2">
                                   <input type="date" className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none" value={auditDateFrom} onChange={e => setAuditDateFrom(e.target.value)} title="Data inicial" />
                                   <span className="text-slate-400 text-sm">até</span>
                                   <input type="date" className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none" value={auditDateTo} onChange={e => setAuditDateTo(e.target.value)} title="Data final" />
                               </div>
                               <button type="button" onClick={() => { setAuditSearch(''); setAuditStatusFilter(''); setAuditDateFrom(''); setAuditDateTo(''); setAuditActorFilter(''); }} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-xl text-sm font-medium transition-colors">Limpar filtros</button>
                           </div>
                       </div>
                       <div className="overflow-x-auto">
                           <table className="w-full text-left">
                               <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-bold"><tr><th className="p-3 sm:p-4">Data</th><th className="p-3 sm:p-4">Responsável</th><th className="p-3 sm:p-4">Ação</th><th className="p-3 sm:p-4">Item</th><th className="p-3 sm:p-4">Status</th></tr></thead>
                               <tbody className="divide-y divide-slate-100 text-sm">
                                   {filteredAuditLogs.length === 0 ? (
                                       <tr><td colSpan={5} className="p-8 text-center text-slate-500">Nenhum evento encontrado com os filtros aplicados.</td></tr>
                                   ) : filteredAuditLogs.map(log => (
                                       <tr key={log.eventId} className="hover:bg-slate-50">
                                           <td className="p-3 sm:p-4 font-mono text-xs text-slate-500">{formatDate(log.date)}</td>
                                           <td className="p-3 sm:p-4 font-medium text-indigo-600">{log.actor}</td>
                                           <td className="p-3 sm:p-4 text-slate-600 max-w-[180px] sm:max-w-none truncate sm:overflow-visible sm:whitespace-normal" title={log.note}>{log.note}</td>
                                           <td className="p-3 sm:p-4 font-bold text-slate-700">{log.itemDescription}</td>
                                           <td className="p-3 sm:p-4"><StatusBadge status={log.status} /></td>
                                       </tr>
                                    ))}
                               </tbody>
                           </table>
                       </div>
                   </div>

                   {/* --- Registros de Exclusão --- */}
                   {deletionAuditLogs.length > 0 && (
                       <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                           <div className="p-4 sm:p-6 border-b border-red-100/60 bg-gradient-to-r from-red-50/50 to-rose-50/30">
                               <div className="flex items-center justify-between gap-4">
                                   <div className="flex items-center gap-3">
                                       <div className="p-2 bg-red-100 rounded-xl flex-shrink-0">
                                           <Trash2 className="w-4 h-4 text-red-600" />
                                       </div>
                                       <div>
                                           <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                               Registros de Exclusão
                                               <span className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{deletionAuditLogs.length}</span>
                                           </h3>
                                           <p className="text-xs text-slate-400 mt-0.5">Entregas permanentemente excluídas. Clique em uma linha para ver os detalhes.</p>
                                       </div>
                                   </div>
                               </div>
                           </div>
                           <div className="overflow-x-auto">
                               <table className="w-full text-left">
                                   <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-bold tracking-wider">
                                       <tr>
                                           <th className="p-3 sm:p-4">Data Exclusão</th>
                                           <th className="p-3 sm:p-4">Excluído por</th>
                                           <th className="p-3 sm:p-4">Entrega</th>
                                           <th className="p-3 sm:p-4">Destinatário</th>
                                           <th className="p-3 sm:p-4 text-center">Detalhes</th>
                                       </tr>
                                   </thead>
                                   <tbody className="divide-y divide-slate-100 text-sm">
                                       {deletionAuditLogs.map(log => (
                                           <tr
                                               key={log.id}
                                               className="hover:bg-red-50/40 transition-colors cursor-pointer group"
                                               onClick={() => setAuditDetailModal({ show: true, log })}
                                           >
                                               <td className="p-3 sm:p-4 font-mono text-xs text-slate-500">{formatDate(log.deletedAt)}</td>
                                               <td className="p-3 sm:p-4 font-medium text-red-700">{String(log.deletedBy || '-')}</td>
                                               <td className="p-3 sm:p-4 font-bold text-slate-700 max-w-[160px] truncate sm:max-w-none sm:overflow-visible sm:whitespace-normal" title={String(log.snapshot?.description || '-')}>{String(log.snapshot?.description || '-')}</td>
                                               <td className="p-3 sm:p-4 text-slate-600">{String(log.snapshot?.owner_name || '-')}</td>
                                               <td className="p-3 sm:p-4 text-center">
                                                   <button
                                                       type="button"
                                                       onClick={(e) => { e.stopPropagation(); setAuditDetailModal({ show: true, log }); }}
                                                       className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors opacity-60 group-hover:opacity-100"
                                                       title="Ver detalhes"
                                                   >
                                                       <Eye className="w-4 h-4" />
                                                   </button>
                                               </td>
                                           </tr>
                                       ))}
                                   </tbody>
                               </table>
                           </div>
                       </div>
                   )}
               </div>
            )}

            {view === 'users' && hasRole(userProfile, 'admin') && (
                <div className="space-y-6">
                    <div className="bg-white p-4 sm:p-6 rounded-2xl border border-slate-200 shadow-sm">
                        <h3 className="text-lg font-bold text-slate-800">Usuários com acesso ao AMC Log</h3>
                        <p className="text-slate-500 text-sm mt-1">Lista de destinatários (colaboradores/admin). A gestão de acessos é feita no painel de controle.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {recipients.length === 0 ? (
                            <p className="text-slate-500 col-span-full">Nenhum usuário carregado. Verifique se as regras do Firestore permitem leitura da lista de usuários com role em sistema_amc_log.</p>
                        ) : recipients.map(r => (
                            <div key={r.id} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-xl">{String(getDisplayName(r))[0]}</div>
                                    <div><h4 className="font-bold text-slate-800">{getDisplayName(r)}</h4><p className="text-xs text-slate-500">{r.email || r.username || r.id}</p></div>
                                </div>
                                <div className="space-y-2">
                                    {/* Depto: exibição desativada por enquanto */}
                                    <div className="flex justify-between text-xs"><span className="text-slate-400">Função:</span> <span className="font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">{getRoleLabel(r)}</span></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

          </div>
        </main>

        {/* --- MODAIS --- */}
        
        {/* Security Modal */}
        {securityModal.show && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in-up">
                <div className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                    {securityCheckSuccess && (
                      <div role="status" aria-live="polite" className="absolute inset-0 bg-white/95 z-10 flex flex-col items-center justify-center gap-3 animate-fade-in-up">
                        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center animate-success-pulse">
                          <CheckCircle className="w-10 h-10 text-emerald-600" aria-hidden />
                        </div>
                        <p className="text-sm font-bold text-emerald-800">PIN validado com sucesso</p>
                      </div>
                    )}
                    <div className="bg-slate-900 p-6 text-white text-center">
                        <div className="bg-white/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Key className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="font-bold text-lg">Confirmação de Segurança</h3>
                        <p className="text-slate-300 text-xs mt-1">
                            {securitySubtitle}
                        </p>
                    </div>
                    <form onSubmit={confirmSecurityCheck} className="p-6 space-y-4">
                        {securityModal.type === 'owner' && (
                            <>
                                <div className="space-y-2">
                                    <span className="text-xs font-bold text-slate-500 uppercase">Quem Vai Retirar?</span>
                                    <div className="grid grid-cols-2 gap-3">
                                        <label htmlFor="security-mode-owner" className={`cursor-pointer rounded-xl border p-3 text-center transition-all ${!isThirdPartyDelivery ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                                            <input
                                                id="security-mode-owner"
                                                type="radio"
                                                name="securityDeliveryMode"
                                                className="sr-only"
                                                checked={!isThirdPartyDelivery}
                                                onChange={() => setSecurityModal(prev => ({ ...prev, deliveryMode: SECURITY_DELIVERY_MODE.OWNER, receiverUid: '' }))}
                                            />
                                            <span className="text-sm font-bold">Destinatário original</span>
                                        </label>
                                        <label htmlFor="security-mode-third-party" className={`cursor-pointer rounded-xl border p-3 text-center transition-all ${isThirdPartyDelivery ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                                            <input
                                                id="security-mode-third-party"
                                                type="radio"
                                                name="securityDeliveryMode"
                                                className="sr-only"
                                                checked={isThirdPartyDelivery}
                                                onChange={() => setSecurityModal(prev => ({ ...prev, deliveryMode: SECURITY_DELIVERY_MODE.THIRD_PARTY }))}
                                            />
                                            <span className="text-sm font-bold">Outro colaborador</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/70 p-4 space-y-2">
                                    <div className="flex items-center justify-between gap-3 text-sm">
                                        <span className="text-slate-500">Destinatário original</span>
                                        <span className="font-bold text-slate-800 text-right">{String(securityModal.delivery?.owner_name || '-')}</span>
                                    </div>
                                    {isThirdPartyDelivery && (
                                        <div className="flex items-center justify-between gap-3 text-sm">
                                            <span className="text-slate-500">Retirado por</span>
                                            <span className="font-bold text-slate-800 text-right">{selectedSecurityReceiver ? getDisplayName(selectedSecurityReceiver) : 'Selecione um colaborador'}</span>
                                        </div>
                                    )}
                                </div>

                                {isThirdPartyDelivery && (
                                    <div className="space-y-1">
                                        <label htmlFor="security-receiver" className="text-xs font-bold text-slate-500 uppercase">Quem Está Retirando?</label>
                                        <RecipientCombobox
                                            id="security-receiver"
                                            ariaLabel="Quem está retirando?"
                                            options={alternateRecipients}
                                            value={securityModal.receiverUid}
                                            onChange={(uid) => setSecurityModal((prev) => ({ ...prev, receiverUid: uid }))}
                                            placeholder="Buscar ou selecione quem está retirando..."
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        <div className="space-y-1">
                            <label htmlFor="security-pin" className="text-xs font-bold text-slate-500 uppercase">{securityPinLabel}</label>
                            <input 
                                id="security-pin"
                                aria-label={securityPinLabel}
                                type="password" 
                                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-center tracking-widest font-bold" 
                                autoFocus
                                required 
                                value={confirmPassword} 
                                onChange={e => setConfirmPassword(e.target.value)} 
                            />
                        </div>
                        <div className="flex gap-3">
                            <button type="button" onClick={closeSecurityModal} className="flex-1 py-3 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition-colors">Cancelar</button>
                            <button type="submit" disabled={securityConfirmDisabled || isConfirmingSecurity} className={`flex-1 py-3 rounded-xl font-bold text-white shadow-lg shadow-indigo-200 transition-all ${(securityConfirmDisabled || isConfirmingSecurity) ? 'bg-slate-300 cursor-not-allowed shadow-none' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
                              {isConfirmingSecurity ? (
                                <span className="inline-flex items-center justify-center gap-2"><span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden /> Validando...</span>
                              ) : 'Confirmar'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )}

        {/* Nova Entrega Modal */}
        {showNewDeliveryModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in-up">
                <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                    <div className="bg-indigo-600 p-6 flex justify-between items-center text-white">
                        <h3 className="font-bold text-lg flex items-center gap-2"><Package className="w-5 h-5"/> Registrar Entrada</h3>
                        <button onClick={() => { setShowNewDeliveryModal(false); setNewDelivery({ ownerUid: '', owner_username: '', description: '', initial_destination: 'default' }); setIsInstantDelivery(false); setInstantDeliveryPassword(''); }} className="hover:bg-white/20 p-1 rounded-full transition-colors" aria-label="Fechar"><X className="w-5 h-5"/></button>
                    </div>
                    <form onSubmit={createDelivery} className="p-6 space-y-5">
                        <div className="space-y-1">
                            <label htmlFor="new-delivery-recipient" className="text-xs font-bold text-slate-500 uppercase">Para Quem?</label>
                            <RecipientCombobox
                                id="new-delivery-recipient"
                                options={recipients}
                                value={newDelivery.ownerUid || ''}
                                required
                                onChange={(uid, rec) =>
                                    setNewDelivery((prev) => ({
                                        ...prev,
                                        ownerUid: uid,
                                        owner_username: rec?.email || rec?.username || '',
                                    }))
                                }
                                placeholder="Buscar ou selecione o colaborador..."
                            />
                        </div>
                        <div className="space-y-1"><label className="text-xs font-bold text-slate-500 uppercase">O que chegou?</label><input type="text" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none" required placeholder="Ex: Pacote Amazon, Envelope..." value={newDelivery.description} onChange={e => setNewDelivery({...newDelivery, description: e.target.value})} /></div>
                        <div className="space-y-2"><label className="text-xs font-bold text-slate-500 uppercase">Destino Inicial</label><div className="grid grid-cols-2 gap-3">
                            <label className={`cursor-pointer p-3 rounded-xl border-2 flex flex-col items-center justify-center gap-2 transition-all ${newDelivery.initial_destination === 'default' ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-slate-100 hover:border-slate-300'}`}><input type="radio" name="dest" value="default" className="hidden" checked={newDelivery.initial_destination === 'default'} onChange={() => { setNewDelivery({...newDelivery, initial_destination: 'default'}); setIsInstantDelivery(false); }} /><Building className="w-5 h-5"/> <span className="text-xs font-bold">{hasRole(userProfile, 'recepcao') ? 'Manter Aqui' : 'Enviar Recepção'}</span></label>
                            <label className={`cursor-pointer p-3 rounded-xl border-2 flex flex-col items-center justify-center gap-2 transition-all ${newDelivery.initial_destination === 'dono' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-100 hover:border-slate-300'}`}><input type="radio" name="dest" value="dono" className="hidden" checked={newDelivery.initial_destination === 'dono'} onChange={() => setNewDelivery({...newDelivery, initial_destination: 'dono'})} /><UserCheck className="w-5 h-5"/> <span className="text-xs font-bold">Direto ao Dono</span></label>
                        </div></div>
                        
                        {/* OPÇÃO DE ENTREGA IMEDIATA */}
                        {newDelivery.initial_destination === 'dono' && (
                            <div className={`rounded-xl border p-4 animate-fade-in-up transition-colors ${isInstantDelivery ? 'bg-emerald-50 border-emerald-300' : 'bg-orange-50 border-orange-200'}`}>
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <div className="relative flex items-center">
                                        <input type="checkbox" className={`w-5 h-5 cursor-pointer ${isInstantDelivery ? 'accent-emerald-600' : 'accent-orange-600'}`} checked={isInstantDelivery} onChange={(e) => setIsInstantDelivery(e.target.checked)} />
                                    </div>
                                    <div>
                                        <span className={`text-sm font-bold ${isInstantDelivery ? 'text-emerald-800' : 'text-orange-800'}`}>Já entreguei ao dono agora</span>
                                        <p className={`text-[11px] mt-0.5 ${isInstantDelivery ? 'text-emerald-600' : 'text-orange-600'}`}>
                                            {isInstantDelivery
                                                ? 'Digite o PIN do destinatário para finalizar a entrega.'
                                                : 'Deixe desmarcado: o item fica na sua lista para entregar depois com PIN.'}
                                        </p>
                                    </div>
                                </label>
                                {isInstantDelivery && (
                                    <div className="mt-3">
                                        <input 
                                            type="password" 
                                            className="w-full p-3 bg-white border border-emerald-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 font-mono text-center tracking-widest" 
                                            placeholder="PIN do destinatário"
                                            value={instantDeliveryPassword}
                                            onChange={(e) => setInstantDeliveryPassword(e.target.value)}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        <button disabled={isSubmittingDelivery} className={`w-full py-3.5 rounded-xl font-bold text-white shadow-lg active:scale-95 transition-all ${isSubmittingDelivery ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200'}`}>
                          {isSubmittingDelivery ? (
                            <span className="inline-flex items-center justify-center gap-2"><span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden /> Registrando...</span>
                          ) : 'Confirmar Registro'}
                        </button>
                    </form>
                </div>
            </div>
        )}

        {/* Timeline Modal */}
        {showTimelineModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in-up">
                <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
                    <div className="bg-slate-900 p-6 flex justify-between items-center text-white">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/10 rounded-lg"><Search className="w-5 h-5"/></div>
                            <div><h3 className="font-bold">Rastreamento</h3><p className="text-xs text-slate-400">Histórico de Eventos</p></div>
                        </div>
                        <button onClick={() => setShowTimelineModal(null)} className="hover:bg-white/20 p-1 rounded-full transition-colors"><X className="w-5 h-5"/></button>
                    </div>
                    <div className="p-8 overflow-y-auto bg-slate-50 flex-1">
                        <h3 className="font-bold text-xl text-slate-800 mb-6">{deliveries.find(d => d.id === showTimelineModal)?.description}</h3>
                        <Timeline history={deliveries.find(d => d.id === showTimelineModal)?.history || []} />
                    </div>
                </div>
            </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModal.show && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in-up">
                <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
                    <div className="bg-red-600 p-6 text-white text-center">
                        <div className="bg-white/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Trash2 className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="font-bold text-lg">Excluir Entrega</h3>
                        <p className="text-red-100 text-xs mt-1">Esta ação é permanente e não pode ser desfeita.</p>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="rounded-xl border border-red-100 bg-red-50/70 p-4 space-y-2">
                            <div className="flex items-center justify-between gap-3 text-sm">
                                <span className="text-slate-500">Descrição</span>
                                <span className="font-bold text-slate-800 text-right">{String(deleteModal.delivery?.description || '-')}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3 text-sm">
                                <span className="text-slate-500">Destinatário</span>
                                <span className="font-bold text-slate-800 text-right">{String(deleteModal.delivery?.owner_name || '-')}</span>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button type="button" onClick={() => setDeleteModal({ show: false, delivery: null })} disabled={isDeleting} className="flex-1 py-3 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition-colors">Cancelar</button>
                            <button type="button" onClick={handleDeleteDelivery} disabled={isDeleting} className={`flex-1 py-3 rounded-xl font-bold text-white shadow-lg shadow-red-200 transition-all ${isDeleting ? 'bg-slate-300 cursor-not-allowed shadow-none' : 'bg-red-600 hover:bg-red-700'}`}>{isDeleting ? 'Excluindo...' : 'Excluir'}</button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Audit Detail Modal */}
        {auditDetailModal.show && auditDetailModal.log && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in-up">
                <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                    <div className="bg-slate-900 p-5 sm:p-6 flex justify-between items-center text-white flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-red-500/20 rounded-xl flex-shrink-0">
                                <Trash2 className="w-5 h-5 text-red-300" />
                            </div>
                            <div>
                                <h3 className="font-bold text-base">Detalhes da Exclusão</h3>
                            </div>
                        </div>
                        <button onClick={() => setAuditDetailModal({ show: false, log: null })} className="hover:bg-white/20 p-1.5 rounded-full transition-colors flex-shrink-0" aria-label="Fechar">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="overflow-y-auto flex-1 p-5 sm:p-6 space-y-4 bg-slate-50">
                        {/* Metadados da exclusão */}
                        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Ação de Exclusão</h4>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">Excluído por</span>
                                <span className="font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded-lg">{String(auditDetailModal.log.deletedBy || '-')}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">Data e hora</span>
                                <span className="font-mono text-slate-700">{formatDate(auditDetailModal.log.deletedAt)}</span>
                            </div>
                        </div>

                        {/* Snapshot da entrega */}
                        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Dados da Entrega</h4>
                            <div className="flex items-start justify-between text-sm gap-3">
                                <span className="text-slate-500 flex-shrink-0">Descrição</span>
                                <span className="font-bold text-slate-800 text-right">{String(auditDetailModal.log.snapshot?.description || '-')}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">Destinatário</span>
                                <span className="font-medium text-slate-700">{String(auditDetailModal.log.snapshot?.owner_name || '-')}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">Registrada em</span>
                                <span className="font-mono text-slate-600">{formatDate(auditDetailModal.log.snapshot?.createdAt)}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">Status final</span>
                                <span className="font-medium text-slate-700">
                                    {auditDetailModal.log.snapshot?.status
                                        ? (STATUS_CONFIG[auditDetailModal.log.snapshot.status]?.label || auditDetailModal.log.snapshot.status)
                                        : '-'}
                                </span>
                            </div>
                        </div>

                        {/* Histórico de movimentação */}
                        {Array.isArray(auditDetailModal.log.snapshot?.history) && auditDetailModal.log.snapshot.history.length > 0 && (
                            <div className="bg-white rounded-xl border border-slate-200 p-4">
                                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Histórico de Movimentação</h4>
                                <Timeline history={auditDetailModal.log.snapshot.history} />
                            </div>
                        )}
                    </div>

                    {/* Rodapé */}
                    <div className="p-4 border-t border-slate-100 bg-white flex-shrink-0 flex justify-between items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setAuditDetailModal({ show: false, log: null })}
                            className="px-5 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition-colors"
                        >
                            Fechar
                        </button>
                        {isGlobalAdmin(userProfile) && (
                            <button
                                type="button"
                                onClick={() => handleDeleteAuditLog(auditDetailModal.log.id)}
                                disabled={isDeletingAuditLog}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-white shadow-lg shadow-red-200 transition-all ${isDeletingAuditLog ? 'bg-slate-300 cursor-not-allowed shadow-none' : 'bg-red-600 hover:bg-red-700 active:scale-95'}`}
                            >
                                <Trash2 className="w-4 h-4" />
                                {isDeletingAuditLog ? 'Removendo...' : 'Apagar registro'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        )}

      </div>
    </>
  );
}


